import { useMemo } from 'react';
import { useQuery } from 'react-query';
import moment from 'moment';
import { getHudVouchers } from '../../services/hudVouchersService';
import generalAffordableMessages from '../../containers/GeneralAffordable/messages';

const APPROVED_OR_PAID_STATUSES = new Set(['Approved', 'Paid']);
const FORWARD_WINDOW_MONTHS = 24;
const VOUCHERS_PAGE_SIZE = 200;
const EMPTY_ROWS = [];

// Stable identity so consumers don't recompute when there's nothing to block.
const EMPTY_BLOCKED_YEAR_MONTHS: Set<string> = new Set();

// Every page of the window: a page holds at most 200 rows, so a property with many contracts would
// otherwise lose the vouchers past the first page.
const getAllHudVoucherRows = async (
  {
    organizationId,
    propertyId,
    params,
  }: { organizationId: string, propertyId: string, params: Object },
  page: number = 1,
): Promise<Array<Object>> => {
  const response = await getHudVouchers({
    organizationId,
    propertyId,
    params: { ...params, limit: VOUCHERS_PAGE_SIZE, page },
  });
  const rows = response?.results || EMPTY_ROWS;
  const totalCount = response?.meta?.totalCount ?? 0;
  if (rows.length === 0 || page * VOUCHERS_PAGE_SIZE >= totalCount) {
    return rows;
  }
  const nextRows = await getAllHudVoucherRows(
    { organizationId, propertyId, params },
    page + 1,
  );
  return [...rows, ...nextRows];
};

type UseHudVoucherApprovedMonthsResult = {
  blockedYearMonths: Set<string>,
  hasVouchersSetUp: boolean,
  isLoading: boolean,
};

const isRowInContractScope = (
  row: { affordableFunding?: ?{ contractNumber?: ?string } },
  contractNumber: ?string,
): boolean =>
  !contractNumber || row.affordableFunding?.contractNumber === contractNumber;

// Turns a picked date into the "YYYY-MM" key used in `blockedYearMonths`.
export const getAvdYearMonthKey = (value: mixed): string | null =>
  value ? moment.utc(value).format('YYYY-MM') : null;

// True when the picked value falls on an Approved/Paid voucher month-year.
// The calendar itself is never disabled for this — only validation blocks it.
export const isAvdOnApprovedVoucherMonth = (
  value: mixed,
  blockedYearMonths: Set<string>,
): boolean => {
  const monthKey = getAvdYearMonthKey(value);
  return Boolean(monthKey && blockedYearMonths?.has(monthKey));
};

// react-hook-form `rules.validate`-shaped: returns `true` when valid, or the
// shared rejection message when the picked month-year already has an
// Approved/Paid voucher.
export const validateAvdOnApprovedVoucherMonth = (
  value: mixed,
  blockedYearMonths: Set<string>,
  intl: { formatMessage: (message: Object) => string },
): true | string => {
  if (isAvdOnApprovedVoucherMonth(value, blockedYearMonths)) {
    return intl.formatMessage(
      generalAffordableMessages.avdApprovedVoucherError,
    );
  }
  return true;
};

const useHudVoucherApprovedMonths = ({
  organizationId,
  propertyId,
  fromYearMonth,
  contractNumber,
  enabled = true,
}: {
  organizationId: string,
  propertyId: string,
  // Floors the query window for surfaces that allow back-dating AVD before
  // "now" (e.g. to the cert's voucherEffectiveDate). Defaults to "now".
  fromYearMonth?: mixed,
  contractNumber?: string | null,
  // Callers gate this on the hud-manage-vouchers flag; when false the query
  // never fires and results fall back to their empty defaults below.
  enabled?: boolean,
}): UseHudVoucherApprovedMonthsResult => {
  // moment.utc(undefined) defaults to "now" — moment.utc(null) does not.
  const { voucherYearMonthFrom, voucherYearMonthTo } = useMemo(() => {
    const from = moment.utc(fromYearMonth || undefined).startOf('month');
    // Anchored to "now", not `from` — a far-past fromYearMonth would
    // otherwise push the window's end before today.
    const now = moment.utc().startOf('month');
    // .clone() matters: moment.max can return the exact `from` (or `now`)
    // reference rather than a new instance, and moment objects mutate
    // in place — without cloning, chaining .add() here would also silently
    // shift `from` itself, corrupting the voucherYearMonthFrom read below.
    const to = moment
      .max(from, now)
      .clone()
      .add(FORWARD_WINDOW_MONTHS, 'months');
    return {
      voucherYearMonthFrom: from.format('YYYY-MM'),
      voucherYearMonthTo: to.format('YYYY-MM'),
    };
  }, [fromYearMonth]);

  const { data, isLoading } = useQuery(
    [
      'hudVouchers',
      'approvedMonths',
      organizationId,
      propertyId,
      voucherYearMonthFrom,
      voucherYearMonthTo,
    ],
    () =>
      getAllHudVoucherRows({
        organizationId,
        propertyId,
        params: { voucherYearMonthFrom, voucherYearMonthTo },
      }),
    {
      enabled: Boolean(organizationId && propertyId) && enabled,
      refetchOnWindowFocus: true,
    },
  );

  return useMemo(() => {
    const rows = data || EMPTY_ROWS;
    // Filtered client-side rather than via a submissionStatus query param.
    const approvedOrPaidRows = rows.filter(
      (row) =>
        APPROVED_OR_PAID_STATUSES.has(row?.submissionStatus) &&
        isRowInContractScope(row, contractNumber),
    );
    const blockedYearMonths = approvedOrPaidRows.length
      ? new Set(
          approvedOrPaidRows
            .map((row) => row?.voucherYearMonth)
            .filter(Boolean),
        )
      : EMPTY_BLOCKED_YEAR_MONTHS;

    return {
      blockedYearMonths,
      hasVouchersSetUp: rows.length > 0,
      isLoading,
    };
  }, [data, isLoading, contractNumber]);
};

export default useHudVoucherApprovedMonths;
